import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Phone, Award, Clock, Tag } from "lucide-react";
import { TRAININGS, CONTACT } from "../data";
import { whatsappLink } from "../whatsapp";
import WhatsAppIcon from "../components/WhatsAppIcon";

// Theme tokens per training programme — mirrors the poster's accent colour.
const THEMES = {
  green: {
    frame: "bg-[#F2F6E4]",
    border: "border-[#5B8C2A]/30",
    accent: "text-[#3D5F1B]",
    badge: "bg-[#5B8C2A] text-white",
    rule: "bg-[#5B8C2A]",
  },
  amber: {
    frame: "bg-[#FBEFE2]",
    border: "border-[#B6671F]/25",
    accent: "text-[#7A4314]",
    badge: "bg-[#B6671F] text-white",
    rule: "bg-[#B6671F]",
  },
  blue: {
    frame: "bg-[#E8F1FA]",
    border: "border-[#1F6FB2]/25",
    accent: "text-[#1F4E84]",
    badge: "bg-[#1F6FB2] text-white",
    rule: "bg-[#1F6FB2]",
  },
};

export default function Workshop() {
  return (
    <div data-testid="workshop-page" className="relative">
      {/* HERO */}
      <section className="relative bg-sageDeep text-bone overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.09] pointer-events-none"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.82' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
          }}
          aria-hidden
        />
        <span className="stamp-num absolute -top-2 right-6 md:right-12 text-bone/10">N° 04</span>

        <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-12 md:pt-20 pb-20 md:pb-24 grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-7">
            <p className="section-num text-bone/55">
              <strong className="text-butter">04</strong> Training programmes
            </p>
            <h1 className="font-display mt-5 text-display-xl text-bone tracking-tightest">
              Become a
              <br />
              <span className="font-italic text-butter">mushroom entrepreneur.</span>
            </h1>
            <p className="mt-7 text-bone/80 max-w-xl leading-relaxed text-[15px]">
              Three programmes from beginner to advanced. Hands-on at our
              Nagpur unit — covering substrate, spawning, harvesting, marketing
              and the economics of running your own mushroom unit.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <a
                href={`tel:+91${CONTACT.phones[1].replace(/\s/g, "")}`}
                data-testid="workshop-call-cta"
                className="btn-primary bg-butter text-ink hover:bg-bone"
              >
                Call to register
                <span className="knob bg-ink text-butter">
                  <Phone className="h-4 w-4" />
                </span>
              </a>
              <a
                href={whatsappLink("Hi Organo Mushroom 👋\n\nI'd like more details on the training programmes.")}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 text-bone link-underline text-sm font-medium self-center"
              >
                <WhatsAppIcon className="h-4 w-4" /> WhatsApp us
              </a>
            </div>
          </div>

          <div className="md:col-span-5 relative">
            <div className="aspect-[1/2] max-h-[640px] mx-auto rounded-4xl overflow-hidden shadow-hoverCard tilt-l bg-bone p-3 md:p-4">
              <div className="w-full h-full rounded-3xl overflow-hidden bg-bone">
                <img
                  src="/img/training/poster-oyster-milky-paddy.jpg"
                  alt="Training programme poster"
                  className="w-full h-full object-contain"
                />
              </div>
            </div>
            <div className="absolute -bottom-6 -left-6 bg-butter text-ink rounded-full h-36 w-36 grid place-items-center text-center shadow-stamp ring-1 ring-ink/10 hidden md:grid">
              <div className="float-y" style={{ "--rot": "-8deg" }}>
                <Award className="h-5 w-5 mx-auto" />
                <p className="font-display text-[22px] leading-tight mt-1">
                  Certified
                  <br />
                  <span className="font-italic">by Organo</span>
                </p>
                <p className="text-[9px] uppercase tracking-[0.22em] mt-1 opacity-75">
                  on completion
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRAINING TIERS */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
        <div className="grid md:grid-cols-12 gap-6 items-end">
          <div className="md:col-span-8">
            <p className="section-num"><strong>·</strong> Three tracks</p>
            <h2 className="font-display mt-5 text-display-md text-ink tracking-tightest">
              Pick the programme
              <br />
              <span className="font-italic text-coral">that fits your goal.</span>
            </h2>
          </div>
          <p className="md:col-span-4 text-ink/60 text-sm leading-relaxed">
            Each programme uses the official Organo Mushroom curriculum poster.
            Tap a poster to read the full module list.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-3 gap-8 md:gap-10">
          {TRAININGS.map((t) => {
            const theme = THEMES[t.theme] || THEMES.green;
            const discount = t.mrp > t.price
              ? Math.round(((t.mrp - t.price) / t.mrp) * 100)
              : 0;
            return (
              <article
                key={t.id}
                data-testid={`training-card-${t.id}`}
                className={`group relative rounded-4xl overflow-hidden border ${theme.border} bg-boneSoft shadow-soft hover:shadow-hoverCard lift flex flex-col`}
              >
                {/* Poster — natural ~1:2 portrait aspect, object-contain to show whole poster */}
                <div className={`relative ${theme.frame} p-3 md:p-4`}>
                  <span className={`absolute top-6 right-6 z-10 inline-flex items-center px-3 py-1.5 rounded-full text-[10px] uppercase tracking-[0.22em] font-medium shadow-soft ${theme.badge}`}>
                    {t.badge}
                  </span>
                  <div className="aspect-[1/2] rounded-3xl overflow-hidden bg-bone">
                    <img
                      src={t.image}
                      alt={t.title}
                      loading="lazy"
                      className="w-full h-full object-contain transition-transform duration-700 group-hover:scale-[1.02]"
                    />
                  </div>
                  {discount > 0 && (
                    <span className="absolute -bottom-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 rounded-full bg-coral text-white px-4 py-1.5 text-[11px] uppercase tracking-[0.22em] font-medium shadow-stamp whitespace-nowrap">
                      <Tag className="h-3 w-3" /> {discount}% off · register now
                    </span>
                  )}
                </div>

                <div className="p-7 md:p-8 pt-9 flex flex-col flex-grow">
                  <h3 className="font-display text-[24px] md:text-[26px] text-ink leading-tight tracking-tightest">
                    {t.title}
                  </h3>
                  <p className={`font-italic text-lg mt-1.5 ${theme.accent}`}>
                    {t.subtitle}
                  </p>

                  <div className="mt-4 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-muted">
                    <Clock className="h-3 w-3" /> {t.duration}
                  </div>

                  <p className="mt-5 text-ink/65 text-[14px] leading-relaxed">
                    {t.description}
                  </p>

                  <ul className="mt-5 space-y-1.5 text-ink/75 text-[13px]">
                    {t.modules.slice(0, 4).map((m) => (
                      <li key={m} className="flex gap-2.5">
                        <span className={`mt-1.5 h-1 w-1 rounded-full ${theme.rule} flex-shrink-0`} />
                        {m}
                      </li>
                    ))}
                    {t.modules.length > 4 && (
                      <li className="text-muted text-xs pl-4">
                        + {t.modules.length - 4} more modules
                      </li>
                    )}
                  </ul>

                  <div className="mt-auto pt-6 border-t border-ink/5">
                    <p className="text-[10px] uppercase tracking-[0.22em] text-muted">Investment</p>
                    <div className="flex items-baseline gap-2 mt-1.5">
                      <p className="font-display text-[34px] text-ink leading-none tabular-nums tracking-tightest">
                        ₹{t.price.toLocaleString("en-IN")}
                      </p>
                      {t.mrp > t.price && (
                        <span className="text-muted line-through text-sm tabular-nums">
                          ₹{t.mrp.toLocaleString("en-IN")}
                        </span>
                      )}
                    </div>

                    <div className="mt-5 grid grid-cols-2 gap-2.5">
                      <a
                        href={`tel:+91${CONTACT.phones[1].replace(/\s/g, "")}`}
                        className="inline-flex items-center justify-center gap-2 rounded-full bg-ink hover:bg-coral text-bone transition-colors py-3 text-sm font-medium"
                      >
                        <Phone className="h-4 w-4" /> Call
                      </a>
                      <a
                        href={whatsappLink(`Hi Organo Mushroom 👋\n\nI'd like to enroll in:\n${t.title} — ${t.subtitle} (₹${t.price})`)}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center justify-center gap-2 rounded-full bg-whatsapp hover:bg-[#178a45] text-white transition-colors py-3 text-sm font-medium"
                      >
                        <WhatsAppIcon className="h-4 w-4" /> Enroll
                      </a>
                    </div>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-coral text-bone">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-24 grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <h3 className="font-display text-display-md text-bone tracking-tightest">
              Ready to start?
              <br />
              <span className="font-italic text-butter">14 seats per batch.</span>
            </h3>
            <p className="mt-4 text-bone/85 text-[15px]">
              Call {CONTACT.phones[0]} or {CONTACT.phones[1]} to reserve.
              We'll send the directions and packing list on WhatsApp.
            </p>
          </div>
          <div className="md:col-span-4 flex md:justify-end gap-3 flex-wrap">
            <a href={`tel:+91${CONTACT.phones[1].replace(/\s/g, "")}`} className="btn-primary bg-bone text-ink hover:bg-butter">
              Call to register
              <span className="knob bg-ink text-butter">
                <Phone className="h-4 w-4" />
              </span>
            </a>
            <Link to="/contact" className="link-underline inline-flex items-center gap-2 text-bone text-sm font-medium self-center">
              Contact us <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
